import React, { useState, useRef, useEffect } from "react";
import { cn } from "../lib/utils";
import { ChevronDown, X } from "lucide-react";

interface SearchableSelectProps {
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  theme: string;
  className?: string;
  inputStyleProp?: string;
  listStyleProp?: string;
  clearable?: boolean;
}

export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = "Select or type...",
  disabled = false,
  theme,
  className,
  inputStyleProp,
  listStyleProp,
  clearable = true,
}: SearchableSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [openUpwards, setOpenUpwards] = useState(false);

  // Find the selected option label based on value
  const selectedOption = options.find((opt) => opt.value === value);

  // Update input value when selected option changes
  useEffect(() => {
    if (selectedOption) {
      setInputValue(selectedOption.label);
    } else {
      setInputValue("");
    }
  }, [selectedOption, value]);

  const filteredOptions = options.filter((opt) =>
    opt?.label?.toLowerCase()?.includes(inputValue?.toLowerCase()),
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        // Reset input to selected value if clicking outside
        if (selectedOption) {
          setInputValue(selectedOption.label);
        } else {
          setInputValue("");
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [selectedOption]);

  useEffect(() => {
    if (isOpen && listRef.current && highlightedIndex >= 0) {
      const element = listRef.current.children[highlightedIndex] as HTMLElement;
      if (element) {
        element.scrollIntoView({ block: "nearest" });
      }
    }
  }, [highlightedIndex, isOpen]);

  const checkDirection = () => {
    if (!wrapperRef.current) return;

    const rect = wrapperRef.current.getBoundingClientRect();
    const dropdownHeight = 300; // same as your maxHeight
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    // If not enough space below but enough above → open upwards
    if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
      setOpenUpwards(true);
    } else {
      setOpenUpwards(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    // Open dropdown when user starts typing
    // if (!isOpen && newValue) {
      setIsOpen(true);
    // }
  };

  const handleInputFocus = () => {
    checkDirection();
    setInputValue("");
    setIsOpen(true);
    inputRef.current?.select();
  };

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        setIsOpen(true);
      }
      // Prevent form submission on Enter when dropdown is closed
      if (e.key === "Enter") {
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredOptions.length - 1 ? prev + 1 : prev,
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          const selected = filteredOptions[highlightedIndex];
          onChange(selected.value);
          setInputValue(selected.label);
          setIsOpen(false);
        }
        break;
      case "Escape":
        setIsOpen(false);
        // Reset to selected value on escape
        if (selectedOption) {
          setInputValue(selectedOption.label);
        }
        break;
      case "Tab":
        setIsOpen(false);
        break;
    }
  };

  const handleOptionClick = (optionValue: string, optionLabel: string) => {
    onChange(optionValue);
    setInputValue(optionLabel);
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange("");
    setInputValue("");
    setIsOpen(false);
  };

  const defaultInputStyle = cn(
    inputStyleProp ||
      "w-full p-4 rounded-xl border-2 text-base font-medium transition-all shadow-md",
    "pr-10", // Space for the dropdown icon
    theme === "dark"
      ? "bg-gray-700/70 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
      : "bg-white dark:bg-slate-900 border-gray-300 dark:border-slate-600 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-slate-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed",
  );

  return (
    <div ref={wrapperRef} className={cn("relative w-full", className)}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleInputKeyDown}
          disabled={disabled}
          placeholder={placeholder}
          className={defaultInputStyle}
          autoComplete="off"
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {clearable && value && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className={cn(
                "p-1 rounded-full transition-colors",
                theme === "dark"
                  ? "hover:bg-gray-600 text-gray-400"
                  : "hover:bg-gray-200 dark:hover:bg-slate-800 text-gray-500 dark:text-slate-400",
              )}
              tabIndex={-1}
            >
              <X className="w-4 h-4" />
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              if (!disabled) {
                checkDirection();
                setIsOpen(prev => !prev);
              }
            }}
            className={cn(
              "p-1 rounded-full transition-transform",
              isOpen && "rotate-180",
              disabled
                ? "opacity-50 cursor-not-allowed"
                : theme === "dark"
                  ? "hover:bg-gray-600"
                  : "hover:bg-gray-200 dark:hover:bg-slate-800",
            )}
            tabIndex={-1}
          >
            <ChevronDown
              className={cn(
                "w-5 h-5",
                theme === "dark" ? "text-gray-400" : "text-gray-500 dark:text-slate-400",
              )}
            />
          </button>
        </div>
      </div>

      {isOpen && !disabled && (
        <div
          className={cn(
            "absolute z-[100] w-full mt-2 rounded-xl border-2 shadow-xl overflow-auto",
            openUpwards ? "bottom-full mb-2" : "top-full mt-2",
            theme === "dark"
              ? "bg-gray-800 border-gray-700"
              : "bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-700",
          )}
          style={{ maxHeight: "300px", zIndex: 1000 }}
        >
          <ul ref={listRef} className="max-h-60 overflow-y-auto py-1">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <li
                  key={option.value}
                  onClick={() => handleOptionClick(option.value, option.label)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  className={cn(
                    listStyleProp || "px-4 py-3 cursor-pointer transition-colors",
                    index === highlightedIndex &&
                      (theme === "dark" ? "bg-gray-700" : "bg-blue-50 dark:bg-blue-900/30"),
                    option.value === value &&
                      (theme === "dark" ? "bg-blue-900/30" : "bg-blue-100 dark:bg-blue-900/30"),
                    theme === "dark"
                      ? "text-white hover:bg-gray-700"
                      : "text-gray-900 dark:text-white hover:bg-blue-50 dark:hover:bg-blue-900/30",
                  )}
                >
                  {option.label}
                </li>
              ))
            ) : (
              <li
                className={cn(
                  listStyleProp || "px-4 py-3 text-center",
                  theme === "dark" ? "text-gray-400" : "text-gray-500 dark:text-slate-400",
                )}
              >
                No matching options found
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
